import {Editor} from '@tinymce/tinymce-react'
import {ErrorMessage} from 'formik'
import React, {useRef, useState, useCallback, useEffect} from 'react'
import {handleLabelNameChanges} from '../../automation'
import {processHtmlForA11y} from '../../../utils/processHtmlForA11y'
import {BLOCKING_TYPES} from '../../../utils/a11yMiddleware'
import {getA11yAiProvider, isAiEnabled} from '../../../utils/ai/aiSuggest'
import {runAxeScan} from '../../../utils/axe/runAxeScan'
import MediaManagerModal from '../MediaManagerModal'

type Props = {
  containerClassName?: string
  label?: any
  limit?: number
  name: string
  initialValue?: any
  rules?: any
  input?: any
  height?: number | undefined
  handleChange: any
  labelClassName?: any
  required?: boolean
  errors?: any
  value?: any
  onA11yError?: (message: string) => void
}

const GENERIC_LINK_TEXTS = new Set(['click here', 'here', 'read more', 'learn more', 'this link'])
const HEADING_SKIP_CLASS = 'tinymce-a11y-warn-heading'
const LINK_WARN_CLASS = 'tinymce-a11y-warn-link'

function findIssueElement(body: HTMLElement, type: string, context?: string): Element | null {
  const candidates = ((): Element[] => {
    switch (type) {
      case 'empty-alt': return Array.from(body.querySelectorAll('img[alt=""], img:not([alt])'))
      case 'image-link-no-alt': return Array.from(body.querySelectorAll('a')).filter(a => {
        const img = a.querySelector('img')
        return !!(img && (!img.getAttribute('alt') || img.getAttribute('alt') === ''))
      })
      case 'empty-link': return Array.from(body.querySelectorAll('a')).filter(
        a => !a.textContent?.trim() && !a.querySelector('img')
      )
      case 'dead-link': return Array.from(body.querySelectorAll('a')).filter(a => {
        const href = (a.getAttribute('href') ?? '').trim()
        return href === '#' || href === '' || href.toLowerCase().startsWith('javascript:')
      })
      case 'new-tab-no-warning': return Array.from(body.querySelectorAll('a[target="_blank"]'))
      case 'heading-skip': return Array.from(body.querySelectorAll('.tinymce-a11y-warn-heading'))
      case 'generic-link-text': return Array.from(body.querySelectorAll('.tinymce-a11y-warn-link'))
      case 'table-no-headers': return Array.from(body.querySelectorAll('table')).filter(
        t => t.querySelectorAll('th').length === 0
      )
      case 'table-empty-header': return Array.from(body.querySelectorAll('th')).filter(
        th => !th.textContent?.trim()
      )
      case 'table-no-caption': return Array.from(body.querySelectorAll('table')).filter(
        t => !t.querySelector('caption')
      )
      case 'table-merged-no-headers': return Array.from(
        body.querySelectorAll('td[colspan], td[rowspan], th[colspan], th[rowspan]')
      ).filter(c => !c.hasAttribute('headers'))
      case 'table-nested': return Array.from(body.querySelectorAll('td table, th table'))
      default: return []
    }
  })()
  if (!context || candidates.length <= 1) return candidates[0] ?? null
  const match = candidates.find(el => el.outerHTML.startsWith(context.slice(0, 40)))
  return match ?? candidates[0] ?? null
}

const FormTinyMce: React.FC<Props> = ({
  containerClassName,
  limit,
  labelClassName,
  label,
  name,
  initialValue,
  rules,
  input,
  height,
  handleChange,
  required,
  errors,
  value,
  onA11yError,
  ...rest
}) => {
  const editorRef = useRef<any>(null)
  const [mediaManagerModalOpen, setMediaManagerModalOpen] = useState(false)
  const [editorCallBack, setEditorCallBack] = useState<any>()
  const [blockingError, setBlockingError] = useState<string | null>(null)
  const [aiBusy, setAiBusy] = useState(false)
  const [axeScanning, setAxeScanning] = useState(false)
  const [axeError, setAxeError] = useState(false)

  const handleCloseMediaManagerModal = () => setMediaManagerModalOpen(false)

  const callingFunction = (cb: any) => {
    const callBackFile = {
      callback: (downloadLink: string, meta: {title: string}) => {
        cb(downloadLink, meta)
      },
    }
    setEditorCallBack(callBackFile)
  }

  const validateA11y = useCallback((): string | null => {
    const editor = editorRef.current
    if (!editor) return null

    const body = editor.getBody() as HTMLElement

    const badImages = Array.from(body.querySelectorAll('img')).filter(
      img => !img.getAttribute('alt') || img.getAttribute('alt') === ''
    )
    if (badImages.length > 0) {
      const msg = `Image needs alt text — double-click the image in the editor to add it (${badImages.length} image${badImages.length > 1 ? 's' : ''} affected)`
      setBlockingError(msg)
      onA11yError?.(msg)
      return msg
    }

    const emptyLinks = Array.from(body.querySelectorAll('a')).filter(
      a => !a.textContent?.trim() && !a.querySelector('img')
    )
    if (emptyLinks.length > 0) {
      const msg = 'A link has no text — add descriptive text to all links'
      setBlockingError(msg)
      onA11yError?.(msg)
      return msg
    }

    const headings = Array.from(body.querySelectorAll('h1,h2,h3,h4,h5,h6'))
    for (let i = 1; i < headings.length; i++) {
      const prev = parseInt(headings[i - 1].tagName[1], 10)
      const curr = parseInt(headings[i].tagName[1], 10)
      if (curr - prev > 1) {
        const msg = `Heading skips from H${prev} to H${curr} — use H${prev + 1} instead (highlighted in amber)`
        setBlockingError(msg)
        onA11yError?.(msg)
        return msg
      }
    }

    setBlockingError(null)
    return null
  }, [onA11yError])

  useEffect(() => {
    const onApplyFixes = () => {
      const editor = editorRef.current
      if (!editor) return
      const fixed = processHtmlForA11y(editor.getContent(), {applyAllFixes: true})
      editor.setContent(fixed.html)
    }
    window.addEventListener('a11y/applyFixes', onApplyFixes)
    return () => window.removeEventListener('a11y/applyFixes', onApplyFixes)
  }, [])

  useEffect(() => {
    const onHighlight = (e: Event) => {
      const editor = editorRef.current
      if (!editor) return
      const {type, context} = (e as CustomEvent).detail as {type: string; context?: string}
      const body = editor.getBody() as HTMLElement
      const el = findIssueElement(body, type, context)
      if (el) {
        editor.selection.select(el)
        editor.focus()
        el.scrollIntoView({behavior: 'smooth', block: 'center'})
      }
    }
    window.addEventListener('a11y/highlightIssue', onHighlight)
    return () => window.removeEventListener('a11y/highlightIssue', onHighlight)
  }, [])

  useEffect(() => {
    const dispatchFixed = (t: string, ctx?: string) =>
      window.dispatchEvent(new CustomEvent('a11y/issueFixed', {detail: {type: t, context: ctx}}))
    const dispatchError = (t: string, ctx: string | undefined, msg: string) =>
      window.dispatchEvent(new CustomEvent('a11y/aiSuggestError', {detail: {type: t, context: ctx, message: msg}}))

    const onAiSuggest = async (e: Event) => {
      const editor = editorRef.current
      if (!editor) return
      const {type, context} = (e as CustomEvent).detail as {type: string; context?: string}
      const provider = getA11yAiProvider()
      const body = editor.getBody() as HTMLElement
      const el = findIssueElement(body, type, context)
      if (!el) {
        dispatchError(type, context, 'Could not locate the element in the editor — try clicking it to focus first')
        return
      }

      const ctx = {pageTitle: document.title}
      setAiBusy(true)
      try {
        if (type === 'empty-alt' || type === 'image-link-no-alt') {
          const img = (el.tagName === 'IMG' ? el : el.querySelector('img')) as HTMLImageElement | null
          if (!img) throw new Error('Image element not found')
          const suggestion = await provider.suggestAltText(img.getAttribute('src') || '', ctx)
          img.setAttribute('alt', suggestion.text)
          editor.selection.select(img)
          editor.execCommand('mceImage')
          dispatchFixed(type, context)
        } else if (type === 'generic-link-text' || type === 'empty-link') {
          const a = (el.tagName === 'A' ? el : el.closest('a')) as HTMLAnchorElement | null
          if (!a) throw new Error('Link element not found')
          const suggestion = await provider.suggestLinkText(a.getAttribute('href') || '', a.textContent || '', ctx)
          editor.undoManager.transact(() => {
            a.textContent = suggestion.text
          })
          editor.selection.select(a)
          editor.nodeChanged()
          editor.focus()
          dispatchFixed(type, context)
        } else if (type === 'table-empty-header') {
          const th = (el.tagName === 'TH' ? el : el.querySelector('th')) as HTMLTableCellElement | null
          if (!th) throw new Error('Header cell not found')
          const colIndex = Array.from(th.parentElement?.children ?? []).indexOf(th)
          const table = th.closest('table')
          const columnSamples = table
            ? Array.from(table.querySelectorAll('tr'))
                .slice(1, 6)
                .map(row => (row.children[colIndex] as HTMLElement | undefined)?.textContent?.trim() ?? '')
                .filter(Boolean)
            : []
          const suggestion = await provider.suggestHeaderLabel(columnSamples, ctx)
          editor.undoManager.transact(() => {
            th.textContent = suggestion.text
          })
          editor.nodeChanged()
          dispatchFixed(type, context)
        } else if (type === 'table-no-caption') {
          const table = (el.tagName === 'TABLE' ? el : el.closest('table')) as HTMLTableElement | null
          if (!table) throw new Error('Table element not found')
          const cellSamples = Array.from(table.querySelectorAll('td, th'))
            .slice(0, 8)
            .map(c => c.textContent?.trim() ?? '')
            .filter(Boolean)
          const suggestion = await provider.suggestTableCaption(cellSamples, ctx)
          editor.undoManager.transact(() => {
            const caption = editor.dom.create('caption') as HTMLElement
            caption.textContent = suggestion.text
            table.insertBefore(caption, table.firstChild)
          })
          editor.nodeChanged()
          dispatchFixed(type, context)
        } else {
          dispatchError(type, context, `AI suggestions are not supported for "${type}"`)
        }
      } catch (err: any) {
        dispatchError(type, context, err?.message || 'AI suggestion failed — please try again')
      } finally {
        setAiBusy(false)
      }
    }
    if (isAiEnabled()) {
      window.addEventListener('a11y/aiSuggest', onAiSuggest)
      return () => window.removeEventListener('a11y/aiSuggest', onAiSuggest)
    }
  }, [])

  useEffect(() => {
    setAxeScanning(false)
    setAxeError(false)
  }, [initialValue])

  // Close the A11yWarningPanel when this editor unmounts (modal closes)
  useEffect(() => {
    return () => {
      window.dispatchEvent(new CustomEvent('a11y/editorClosed'))
    }
  }, [])

  const applyInlineWarnings = useCallback((editor: any) => {
    const body = editor.getBody() as HTMLElement
    if (!body) return

    body.querySelectorAll(`.${HEADING_SKIP_CLASS}, .${LINK_WARN_CLASS}`).forEach(el => {
      el.classList.remove(HEADING_SKIP_CLASS, LINK_WARN_CLASS)
      el.removeAttribute('title')
    })

    const headings = Array.from(body.querySelectorAll('h1,h2,h3,h4,h5,h6'))
    for (let i = 1; i < headings.length; i++) {
      const prev = parseInt(headings[i - 1].tagName[1], 10)
      const curr = parseInt(headings[i].tagName[1], 10)
      if (curr - prev > 1) {
        headings[i].classList.add(HEADING_SKIP_CLASS)
        headings[i].setAttribute('title', `Heading jumps from H${prev} to H${curr} — consider H${prev + 1}`)
      }
    }

    body.querySelectorAll('a').forEach(el => {
      const text = el.textContent?.trim().toLowerCase() ?? ''
      if (GENERIC_LINK_TEXTS.has(text)) {
        el.classList.add(LINK_WARN_CLASS)
        el.setAttribute('title', 'Describe where this link goes — avoid "click here" or "read more"')
      }
    })
  }, [])

  return (
    <div
      className={label ? 'mb-6' : 'col-lg-6 '}
      data-cy={`${handleLabelNameChanges(name)}-tiny-editor`}
    >
      {label && (
        <label className={`mb-1 fw-bolder fs-6 ${labelClassName || 'col-lg-4'}`}>
          <span className={required ? 'required' : ''}>{label}</span>
        </label>
      )}
      <Editor
        onInit={(_evt, editor) => {
          editorRef.current = editor
          ;(editor as any).validateA11y = validateA11y
        }}
        initialValue={initialValue}
        value={value}
        tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
        onEditorChange={(e) => {
          handleChange({target: {name, value: e}})
        }}
        data-cy={`${name}-editor-field`}
        init={{
          relative_urls: false,
          remove_script_host: false,
          convert_urls: true,
          height: height ?? 550,
          menubar: false,
          toolbar_mode: 'sliding',
          forced_root_block: 'p',
          image_description: true,
          image_advtab: true,
          plugins:
            'lists advlist anchor autolink link charmap code directionality emoticons image ' +
            'insertdatetime media nonbreaking pagebreak preview searchreplace ' +
            'visualblocks fullscreen help wordcount table ' +
            'collapse',
          toolbar1:
            'bold italic backcolor | alignleft aligncenter alignright alignjustify | ' +
            'styles undo redo | formatselect | blocks fontfamily fontsize | ' +
            'bullist numlist outdent indent | removeformat | help | ' +
            'link unlink openlink | image media | table | insertfile | code | ltr rtl | emoticons | insertdatetime | collapse | a11ycheck',
          external_plugins: {
            collapse: '/tinymce/plugins/collapse/plugin.min.js',
          },
          content_style: `
            body { font-family: Helvetica, Arial, sans-serif; font-size: 14px }
            .${HEADING_SKIP_CLASS} { outline: 2px solid #f59e0b; outline-offset: 2px; }
            .${LINK_WARN_CLASS} { text-decoration: underline wavy #f97316; }
            .collapsible { border: 1px solid #ccc; padding: 10px; margin: 5px 0; transition: max-height 0.2s ease-out; }
            .collapsible.collapsed { max-height: 20px; overflow: hidden; background-color: #f9f9f9; }
          `,
          invalid_elements: 'b,i,font,u,strike,tt,center,big,small',
          automatic_uploads: true,
          extended_valid_elements: 'style,link[href|rel]',
          custom_elements: 'style,link,~link',
          file_picker_types: 'image file',
          file_picker_callback: (_cb: any, _value: any, _meta: any) => {
            setMediaManagerModalOpen(true)
            callingFunction(_cb)
          },
          paste_preprocess: (_plugin: any, args: any) => {
            if (args.content) {
              args.content = processHtmlForA11y(args.content).html
            }
          },
          setup: (editor: any) => {
            editor.on('NodeChange', () => {
              applyInlineWarnings(editor)
            })
            editor.ui.registry.addButton('a11ycheck', {
              text: 'Check accessibility',
              tooltip: 'Scan this content for accessibility issues',
              onAction: async () => {
                const body = editor.getBody() as HTMLElement
                setAxeError(false)
                setAxeScanning(true)
                try {
                  const [axeIssues, {warnings: pipelineWarnings}] = await Promise.all([
                    runAxeScan(body),
                    Promise.resolve(processHtmlForA11y(editor.getContent())),
                  ])
                  const pipelineIssues = pipelineWarnings.map(w => ({
                    type: w.type,
                    message: w.message,
                    context: w.context,
                    impact: BLOCKING_TYPES.has(w.type as any) ? ('critical' as const) : ('moderate' as const),
                  }))
                  window.dispatchEvent(new CustomEvent('a11y/scanResults', {detail: [...pipelineIssues, ...axeIssues]}))
                } catch {
                  // Advisory only: scan failure leaves content + save flow untouched.
                  // Do NOT dispatch an empty scan result here — that would falsely read as "no issues".
                  setAxeError(true)
                } finally {
                  setAxeScanning(false)
                }
              },
            })
          },
        }}
        textareaName={name}
        apiKey='3qyej0twovgwvklxo176yhnorvfpy2f965i3ac4d56gm4fpr'
        {...rest}
      />

      {blockingError && (
        <div
          role='alert'
          className='fv-plugins-message-container text-danger fw-bolder small mt-1'
        >
          ⚠️ {blockingError}
        </div>
      )}

      {aiBusy && (
        <div className='fv-plugins-message-container fw-bolder small mt-1' style={{color: '#7c3aed'}}>
          ✨ Generating suggestion…
        </div>
      )}
      {axeScanning && (
        <div className='fv-plugins-message-container fw-bolder small mt-1' style={{color: '#1e40af'}}>
          🔍 Scanning for accessibility issues…
        </div>
      )}
      {axeError && (
        <div className='fv-plugins-message-container fw-bolder small mt-1' style={{color: '#b45309'}}>
          ⚠️ Accessibility scan unavailable — please try again.
        </div>
      )}

      <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
        <ErrorMessage name={name} component='div' className='field-error-message' />
      </div>

      <MediaManagerModal
        isOpen={mediaManagerModalOpen}
        handleClose={handleCloseMediaManagerModal}
        editorCallBack={editorCallBack}
      />
    </div>
  )
}

export default FormTinyMce
